import { useMemo } from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import { ActivitiesList } from './components/activity-list';
import { ActivityProgress } from './components/activity-progress';
import { PlanCard } from './plan';
import {
  ActivityStatus,
  type Activity,
  type Plan,
} from '@/data/idp/plans/interfaces';
import { activitiesAmountByStatus } from '@/utils/activities-amount-by-status';
import { formatDate } from '@/utils/format-date';

interface InProgressPlanCardProps {
  activities: Activity[];
  loadingActivities: boolean;
  plan: Plan;
}

export const InProgressPlanCard = ({
  activities,
  plan,
}: InProgressPlanCardProps) => {
  const hasActivities = activities.length > 0;

  const activitiesSeparatedByStatus = useMemo(() => {
    return activitiesAmountByStatus(activities);
  }, [activities]);

  return (
    <PlanCard cardActionLabel="Continuar plano" plan={plan} hideTitle>
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={1.5}>
            <Typography variant="h2">
              {plan.title || 'Plano sem título'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Typography fontWeight="semibold">Duração:</Typography>
              <Typography>
                {(plan.duration_in_months &&
                  `${plan.duration_in_months} meses`) ||
                  'Sem duração'}
              </Typography>
            </Stack>
            {plan.estimated_ends_at && (
              <Stack direction="row" spacing={1}>
                <Typography fontWeight="semibold">Fim estimado:</Typography>{' '}
                <Typography data-testid="planCardInProgressDates">
                  {formatDate(plan.estimated_ends_at)}
                </Typography>
              </Stack>
            )}
            {hasActivities && (
              <ActivityProgress
                card
                data={{
                  [ActivityStatus.InProgress]:
                    activitiesSeparatedByStatus[ActivityStatus.InProgress],
                  [ActivityStatus.Completed]:
                    activitiesSeparatedByStatus[ActivityStatus.Completed],
                  [ActivityStatus.Pending]:
                    activitiesSeparatedByStatus[ActivityStatus.Pending],
                }}
                legend={false}
              />
            )}
          </Stack>
        </Grid>
        <Grid
          sx={{ display: { xs: 'none', sm: 'block' } }}
          size={{ xs: 12, md: 8 }}
        >
          <ActivitiesList activities={activities} limit={3} />
        </Grid>
      </Grid>
    </PlanCard>
  );
};
